// Hooks de datos de las Políticas Especiales. Las URLs viven en `api/services/loan-offerings/`.
// SIN MOCK: si la lectura falla, la pantalla lo dice — igual que Promociones.
import { useMemo } from 'react';
import { keepPreviousData, useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import type {
    CreateStorePolicyRequest,
    StorePolicyLogEntryResponse,
    StorePolicyResponse,
    UpdateStorePolicyRequest,
} from '@/api/services/loan-offerings/contract';
import {
    changeStorePolicyStatus,
    createStorePolicy,
    listStorePolicies,
    listStorePolicyLogByPolicy,
    updateStorePolicy,
} from '@/api/services/loan-offerings/loan-offerings.client';
import type { SpecialPolicy, StorePolicyLogEntry, StorePolicyStatus } from '../types';
import { toSpecialPolicy, toStorePolicyLogEntry } from './storePolicyMappers';

/** Raíz de las query keys del tab; invalidarla alcanza al listado y a los historiales. */
export const STORE_POLICIES_KEY = 'store-policies';

const EMPTY_POLICIES: SpecialPolicy[] = [];
/** Página sin `entries`: el schema de salida no la declara obligatoria. */
const EMPTY_WIRE: StorePolicyLogEntryResponse[] = [];

export interface StorePoliciesResult {
    policies: SpecialPolicy[];
    isLoading: boolean;
    error: Error | null;
}

/**
 * El catálogo completo de políticas especiales. Se lee sin filtro de estado a propósito: los
 * filtros de la lista son de cliente y necesitan el lote entero para armar sus combos.
 *
 * `enabled` cablea el gate RBAC: sin `loan.storePolicy.read` disparar el request solo cosecha un
 * 403 que nadie va a mostrar.
 */
export function useStorePolicies(enabled: boolean): StorePoliciesResult {
    const query = useQuery({
        queryKey: [STORE_POLICIES_KEY, 'list'],
        queryFn: () => listStorePolicies(),
        enabled,
        staleTime: 60_000,
        placeholderData: keepPreviousData,
    });

    const policies = useMemo(
        () => (query.data ? query.data.map(toSpecialPolicy) : EMPTY_POLICIES),
        [query.data],
    );

    // Con la query deshabilitada `isLoading` de react-query sigue en `true`: sin esto, el rol sin
    // permiso vería un cargando eterno en vez del aviso.
    return {
        policies,
        isLoading: enabled && query.isLoading,
        error: enabled ? query.error : null,
    };
}

export interface StorePolicyLogResult {
    entries: StorePolicyLogEntry[];
    isLoading: boolean;
    error: Error | null;
}

/**
 * El historial de UNA política, del más reciente al más viejo. Sin `policyId` la query ni se
 * dispara: un borrador sin crear no tiene asientos que leer.
 */
export function useStorePolicyLog(policyId: string | null): StorePolicyLogResult {
    const enabled = policyId !== null && policyId !== '';
    const query = useQuery({
        queryKey: [STORE_POLICIES_KEY, 'log', policyId ?? ''],
        queryFn: () => listStorePolicyLogByPolicy(policyId ?? ''),
        enabled,
        staleTime: 60_000,
        placeholderData: keepPreviousData,
    });

    // Sin política elegida no hay historial: `keepPreviousData` conserva la respuesta de la
    // anterior, y mapearla pintaría su historial en la política nueva.
    const entries = useMemo(
        () => (enabled ? (query.data?.entries ?? EMPTY_WIRE).map(toStorePolicyLogEntry) : []),
        [enabled, query.data],
    );

    return {
        entries,
        isLoading: enabled && query.isLoading,
        error: enabled ? query.error : null,
    };
}

/** Alta de una política → POST /store-policies. Nace en DRAFT. */
export function useCreateStorePolicy() {
    const queryClient = useQueryClient();
    return useMutation<StorePolicyResponse, Error, CreateStorePolicyRequest>({
        mutationFn: (body) => createStorePolicy(body),
        onSuccess: () => queryClient.invalidateQueries({ queryKey: [STORE_POLICIES_KEY] }),
    });
}

export interface UpdateStorePolicyInput {
    policyId: string;
    patch: UpdateStorePolicyRequest;
}

/** Edición parcial → PUT /store-policies/{policyId}. El backend sube `version`. */
export function useUpdateStorePolicy() {
    const queryClient = useQueryClient();
    return useMutation<StorePolicyResponse, Error, UpdateStorePolicyInput>({
        mutationFn: ({ policyId, patch }) => updateStorePolicy(policyId, patch),
        onSuccess: () => queryClient.invalidateQueries({ queryKey: [STORE_POLICIES_KEY] }),
    });
}

export interface ChangeStorePolicyStatusInput {
    policyId: string;
    status: StorePolicyStatus;
    /** Pausar y vencer lo llevan; queda escrito en la bitácora. */
    reason?: string;
}

/** Publicar, pausar, reanudar o vencer → PUT /store-policies/{policyId}/status. */
export function useChangeStorePolicyStatus() {
    const queryClient = useQueryClient();
    return useMutation<StorePolicyResponse, Error, ChangeStorePolicyStatusInput>({
        mutationFn: ({ policyId, status, reason }) =>
            changeStorePolicyStatus(policyId, status, reason),
        onSuccess: () => queryClient.invalidateQueries({ queryKey: [STORE_POLICIES_KEY] }),
    });
}
